/**
 * Typing metrics & helpers
 */

export const calculateWpm = (correctChars, elapsedSeconds) => {
  if (!elapsedSeconds || elapsedSeconds <= 0) return 0;
  const minutes = elapsedSeconds / 60;
  return Math.round((correctChars / 5) / minutes);
};

export const calculateAccuracy = (correctChars, totalTyped) => {
  if (!totalTyped || totalTyped <= 0) return 100;
  return Math.round((correctChars / totalTyped) * 100);
};

/** Net WPM penalizes accuracy — used for game scoring */
export const calculateNetWpm = (correctChars, totalTyped, elapsedSeconds) => {
  const gross = calculateWpm(correctChars, elapsedSeconds);
  const accuracy = calculateAccuracy(correctChars, totalTyped);
  return Math.round(gross * (accuracy / 100));
};

export const formatDuration = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

export const getCharStatus = (typed, target, index) => {
  if (index >= typed.length) return 'pending';
  if (typed[index] === target[index]) return 'correct';
  return 'incorrect';
};

// Complete when the student has typed through the full passage (errors allowed).
// Accuracy/WPM are computed separately in the engines; requiring an exact match
// blocked saves for sentences, paragraphs, stories, and fingering drills.
export const isLessonComplete = (typed, target) =>
  target.length > 0 && typed.length >= target.length;

export const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname.includes('ondigitalocean.app') ||
      window.location.hostname.includes('services.knls.in'))
  ) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

export const getStudentToken = () => localStorage.getItem('student_token');

export const requireStudentAuth = (navigate) => {
  const token = getStudentToken();
  if (!token) {
    navigate('/student/login');
    return false;
  }
  return true;
};

export const parseStudentData = () => {
  try {
    const raw = localStorage.getItem('student_data');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/** Strip emojis/icons for typing practice display only (source LMS content unchanged). */
export const sanitizeStoriesTypingText = (text) => {
  let t = String(text || '');

  t = t
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/[\u2026]/g, '...')
    .replace(/[\u00A0\u1680\u2000-\u200B\u202F\u205F\u3000]/g, ' ')
    .replace(/[\u2022\u2023\u2043\u2219\u25AA\u25AB\u25CF\u25CB\u25A0\u25A1\u25B6\u25C6]/g, ' ')
    .replace(/[\u2190-\u21FF]/g, ' ')
    .replace(/[\u2713\u2714\u2705\u2611\u274E\u2717\u2718\u274C]/g, '');

  t = t.replace(
    /(?:\uD83C[\uDDE6-\uDDFF]\uD83C[\uDDE6-\uDDFF])|(?:[\u{1F300}-\u{1FAFF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{1F900}-\u{1F9FF}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{2300}-\u{23FF}\u{2B50}\u{2B55}\u{2934}\u{2935}\u{3030}\u{303D}\u{3297}\u{3299}])(?:[\uFE00-\uFE0F\u200D\u20E3][\u{1F300}-\u{1FAFF}\u{1F600}-\u{1F64F}\u{2600}-\u{27BF}]*)*/gu,
    '',
  );

  t = t.replace(/[\uFE0F\u200D]/g, '');
  t = t.replace(/[^\t\n\r\x20-\x7E\u0C00-\u0C7F]/g, '');
  t = t.replace(/[ \t]{2,}/g, ' ');
  t = t.replace(/\n{3,}/g, '\n\n');

  return t.trim();
};
